import { Component, input, OnInit, output } from '@angular/core';
import { formControl, FormControlExtended, formGroup, FormGroupExtended, Validators } from '@al00x/forms';
import { ProductAttribute, ProductAttributeDetail, VariantEntity } from '@models';
import { UploadResult } from '../../../../services';
import {
  CacButtonComponent,
  CacFieldComponent,
  CacImageUploaderComponent,
  CacSwitchComponent,
} from '@controladad/ng-base';
import { numberValidator } from '../../../../helpers';
import { ReactiveFormsModule } from '@angular/forms';

export type VariationForm = {
  imagePath: FormControlExtended<string | UploadResult | null>;
  regularPrice: FormControlExtended<number>;
  salePrice: FormControlExtended<number | null>;
  discountExpiresAtUtc: FormControlExtended<string | null>;
  costPrice: FormControlExtended<number>;
  sku: FormControlExtended<string>;
  barcode: FormControlExtended<string>;
  itemCode: FormControlExtended<string>;
  model: FormControlExtended<string>;
  weight: FormControlExtended<number>;
  isManageableStock: FormControlExtended<boolean>;
  stock: FormControlExtended<number>;
  height: FormControlExtended<number>;
  length: FormControlExtended<number>;
  width: FormControlExtended<number>;
  status: FormControlExtended<boolean>;
};

export interface Variation {
  clientId: string; // Unique stable ID for tracking (even before save)
  variationId?: number;
  attributes: ProductAttribute[] | ProductAttributeDetail[];
  form: FormGroupExtended<VariationForm>;
}

@Component({
  selector: 'lib-variation-form',
  imports: [CacImageUploaderComponent, CacFieldComponent, CacSwitchComponent, CacButtonComponent, ReactiveFormsModule],
  templateUrl: './variation-form.component.html',
  styleUrl: './variation-form.component.scss',
})
export class VariationFormComponent implements OnInit {
  variation = input.required<Variation>();
  showActions = input(true);
  stockReadOnly = input(false);
  stockReadOnlyValue = input<number | null | undefined>(undefined);
  regularPriceLabel = input('Regular Price (KWD)');

  delete = output();

  ngOnInit(): void {
    if (!this.variation) {
      throw new Error('Variation input is required');
    }
  }

  static createForm(variation?: VariantEntity): FormGroupExtended<VariationForm> {
    const initialManageable = variation?.isManageableStock === true;

    const form = formGroup({
      imagePath: formControl<string | UploadResult | null>(variation?.image?.url),
      regularPrice: formControl<number>(variation?.regularPrice, [Validators.required, numberValidator()]),
      salePrice: formControl<number | null>(variation?.salePrice, numberValidator()),
      discountExpiresAtUtc: formControl<string | null>(variation?.discountExpiresAtUtc?.slice(0, 16) ?? null),
      costPrice: formControl<number>(variation?.costPrice, numberValidator()),
      sku: formControl<string>(variation?.sku, Validators.required),
      barcode: formControl<string>(variation?.barcode),
      itemCode: formControl<string>(variation?.itemCode),
      model: formControl<string>(variation?.model),
      weight: formControl<number>(variation?.weight, [numberValidator()]),
      isManageableStock: formControl<boolean>(initialManageable),
      stock: formControl<number>(variation?.stock, initialManageable ? [Validators.required, numberValidator(), Validators.min(0)] : []),
      height: formControl<number>(variation?.height, [numberValidator()]),
      length: formControl<number>(variation?.length, [numberValidator()]),
      width: formControl<number>(variation?.width, [numberValidator()]),
      status: formControl<boolean>(true, Validators.required),
    });

    if (variation) form.patchValue({ status: variation.status === 1, isManageableStock: !!variation.isManageableStock });

    // Custom validator to ensure sale price does not exceed regular price
    const salePriceValidator = () => {
      const regularPrice = form.controls.regularPrice.value;
      const salePrice = form.controls.salePrice.value;

      if (salePrice != null && regularPrice != null && salePrice > regularPrice) {
        form.controls.salePrice.setCustomError('Sale price cannot exceed regular price');
        return { salePriceExceedsRegular: true };
      }

      form.controls.salePrice.setCustomError(undefined);
      return null;
    };

    // Add the validator to sale price control
    form.controls.salePrice.addValidators(salePriceValidator);
    form.controls.salePrice.valueChanges.subscribe(value => {
      if (value == null) form.controls.discountExpiresAtUtc.setValue(null);
    });

    // Re-validate sale price when regular price changes
    form.controls.regularPrice.valueChanges.pipe().subscribe(() => {
      form.controls.salePrice.updateValueAndValidity({ emitEvent: false });
    });

    form.controls.isManageableStock.valueChanges.pipe().subscribe((manageable) => {
      form.controls.stock.setValidators(manageable ? [Validators.required, numberValidator(), Validators.min(0)] : []);
      form.controls.stock.updateValueAndValidity({ emitEvent: false });
    });

    return form;
  }
}
